import React, { useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Modal,
  Form,
  Select,
  DatePicker,
  Input,
  InputNumber,
  Button,
  Space,
  Table,
  Tag,
  Divider,
  Typography,
  Row,
  Col,
  Alert,
  Spin,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  SaveOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { createReturnOrderRequest } from '../../redux/actions/returnOrderActions';
import { ordersAPI } from '../../api/config';

const { Option } = Select;
const { Text, Title } = Typography;
const { TextArea } = Input;

const TAX_SLABS = ['0', '5', '12', '18', '28'];

const fmtMoney = (v) => (v != null ? `₹ ${Number(v).toFixed(2)}` : '—');

const calcAmount = (qty, rate, taxSlab) => {
  const q = parseFloat(qty) || 0;
  const r = parseFloat(rate) || 0;
  const t = parseFloat(taxSlab) || 0;
  return q * r * (1 + t / 100);
};

const EMPTY_ITEM = () => ({
  key: Date.now() + Math.random(),
  purchase_order_item_id: undefined,
  product_id: undefined,
  product_name: '',
  unit: '',
  return_quantity: 1,
  rate: 0,
  tax_slab: '18',
  quality_remarks: '',
});

/**
 * ReturnOrderForm
 *
 * Props:
 *  open    — boolean
 *  onClose — callback
 */
const ReturnOrderForm = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { orders } = useSelector((s) => s.orders);
  const { actionLoading } = useSelector((s) => s.returns);

  const [form] = Form.useForm();
  const [items, setItems] = useState([EMPTY_ITEM()]);
  const [selectedPO, setSelectedPO] = useState(null);
  const [poLoading, setPoLoading]   = useState(false);
  const [poItems, setPoItems]       = useState([]);

  const updateItem = (key, field, value) =>
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, [field]: value } : i)));

  const addItem = () => setItems((prev) => [...prev, EMPTY_ITEM()]);

  const removeItem = (key) => {
    if (items.length === 1) return;
    setItems((prev) => prev.filter((i) => i.key !== key));
  };

  const handlePOSelect = async (poId) => {
    setPoLoading(true);
    setPoItems([]);
    form.setFieldValue('delivery_challan_id', undefined);
    try {
      const response = await ordersAPI.getOrder(poId);
      const po = response.data;
      setSelectedPO(po);
      setPoItems(po.items || []);
    } catch {
      setSelectedPO(null);
    } finally {
      setPoLoading(false);
    }
  };

  const handlePoItemSelect = (poItemId, itemKey) => {
    const poItem = poItems.find((i) => i.id === poItemId);
    if (poItem) {
      setItems((prev) =>
        prev.map((i) =>
          i.key === itemKey
            ? {
                ...i,
                purchase_order_item_id: poItemId,
                product_id: poItem.product_id,
                product_name: poItem.product_name || '',
                unit: poItem.unit || '',
                rate: poItem.rate || 0,
                tax_slab: poItem.tax_slab ? String(poItem.tax_slab) : '18',
              }
            : i
        )
      );
    }
  };

  const totalAmount = useMemo(
    () => items.reduce((s, i) => s + calcAmount(i.return_quantity, i.rate, i.tax_slab), 0),
    [items]
  );

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const validItems = items.filter(
        (i) => i.product_id && i.purchase_order_item_id && i.return_quantity > 0 && i.rate > 0
      );
      if (validItems.length === 0) {
        return;
      }
      dispatch(
        createReturnOrderRequest({
          purchase_order_id:   values.purchase_order_id,
          delivery_challan_id: values.delivery_challan_id || undefined,
          return_date:         values.return_date.format('YYYY-MM-DD'),
          return_reason:       values.return_reason,
          remarks:             values.remarks || '',
          items: validItems.map((i) => ({
            purchase_order_item_id: i.purchase_order_item_id,
            product_id:    i.product_id,
            product_name:  i.product_name,
            unit:          i.unit,
            return_quantity: parseFloat(i.return_quantity),
            rate:          parseFloat(i.rate),
            tax_slab:      i.tax_slab,
            quality_remarks: i.quality_remarks || '',
          })),
        })
      );
      handleClose();
    });
  };

  const handleClose = () => {
    form.resetFields();
    setItems([EMPTY_ITEM()]);
    setSelectedPO(null);
    setPoItems([]);
    onClose();
  };

  const itemColumns = [
    {
      title: 'PO Item',
      key: 'po_item',
      width: 180,
      render: (_, record) => (
        <Select
          showSearch
          style={{ width: '100%' }}
          placeholder="Select PO item"
          size="small"
          value={record.purchase_order_item_id}
          onChange={(val) => handlePoItemSelect(val, record.key)}
          disabled={!poItems.length}
          filterOption={(input, option) =>
            option?.children?.toLowerCase().includes(input.toLowerCase())
          }
        >
          {poItems.map((pi) => (
            <Option key={pi.id} value={pi.id}>
              {pi.product_name} ({pi.quantity} {pi.unit})
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: 'Unit',
      key: 'unit',
      width: 70,
      render: (_, record) => (
        <Input
          size="small"
          value={record.unit}
          onChange={(e) => updateItem(record.key, 'unit', e.target.value)}
          placeholder="unit"
        />
      ),
    },
    {
      title: 'Ret Qty',
      key: 'qty',
      width: 85,
      render: (_, record) => (
        <InputNumber
          size="small"
          style={{ width: '100%' }}
          min={0.01}
          precision={2}
          value={record.return_quantity}
          onChange={(val) => updateItem(record.key, 'return_quantity', val)}
        />
      ),
    },
    {
      title: 'Rate (₹)',
      key: 'rate',
      width: 100,
      render: (_, record) => (
        <InputNumber
          size="small"
          style={{ width: '100%' }}
          min={0}
          precision={2}
          value={record.rate}
          onChange={(val) => updateItem(record.key, 'rate', val)}
        />
      ),
    },
    {
      title: 'GST %',
      key: 'tax_slab',
      width: 80,
      render: (_, record) => (
        <Select
          size="small"
          style={{ width: '100%' }}
          value={record.tax_slab}
          onChange={(val) => updateItem(record.key, 'tax_slab', val)}
        >
          {TAX_SLABS.map((s) => <Option key={s} value={s}>{s}%</Option>)}
        </Select>
      ),
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 100,
      align: 'right',
      render: (_, record) => (
        <Text strong style={{ color: '#1890ff', fontSize: 12 }}>
          {fmtMoney(calcAmount(record.return_quantity, record.rate, record.tax_slab))}
        </Text>
      ),
    },
    {
      title: 'Quality Remarks',
      key: 'quality_remarks',
      render: (_, record) => (
        <Input
          size="small"
          value={record.quality_remarks}
          onChange={(e) => updateItem(record.key, 'quality_remarks', e.target.value)}
          placeholder="Describe defect…"
        />
      ),
    },
    {
      title: '',
      key: 'remove',
      width: 36,
      render: (_, record) => (
        <Tooltip title="Remove">
          <Button
            type="text" danger size="small"
            icon={<DeleteOutlined />}
            onClick={() => removeItem(record.key)}
            disabled={items.length === 1}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <Modal
      title={
        <Space>
          <SaveOutlined style={{ color: '#fa8c16' }} />
          Create Return Order
        </Space>
      }
      open={open}
      onCancel={handleClose}
      footer={
        <Space>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="primary"
            danger
            icon={<SaveOutlined />}
            loading={actionLoading}
            onClick={handleSubmit}
          >
            Create Return Order
          </Button>
        </Space>
      }
      width={960}
      destroyOnClose
      styles={{ body: { maxHeight: '75vh', overflowY: 'auto' } }}
    >
      <Form form={form} layout="vertical">
        <Row gutter={16}>
          <Col xs={24} sm={10}>
            <Form.Item
              name="purchase_order_id"
              label="Purchase Order"
              rules={[{ required: true, message: 'PO is required' }]}
            >
              <Select
                showSearch
                placeholder="Search PO…"
                onChange={handlePOSelect}
                filterOption={(input, option) =>
                  String(option?.value).includes(input) ||
                  option?.children?.toLowerCase().includes(input.toLowerCase())
                }
              >
                {(orders || []).map((o) => (
                  <Option key={o.id} value={o.id}>
                    #{o.id} — {o.vendor_name} (₹{Number(o.total_amount).toFixed(0)})
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} sm={7}>
            <Form.Item name="return_date" label="Return Date"
              rules={[{ required: true, message: 'Date required' }]}
              initialValue={dayjs()}
            >
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={7}>
            <Form.Item name="delivery_challan_id" label="Delivery Challan (optional)">
              <InputNumber style={{ width: '100%' }} min={1} precision={0} placeholder="Challan ID" />
            </Form.Item>
          </Col>
        </Row>

        {selectedPO && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message={`PO #${selectedPO.id} — ${selectedPO.vendor_name} | Total: ₹${Number(selectedPO.total_amount).toFixed(2)}`}
          />
        )}
        {poLoading && <div style={{ textAlign: 'center', padding: 12 }}><Spin size="small" /></div>}

        <Form.Item name="return_reason" label="Return Reason"
          rules={[{ required: true, message: 'Reason is required' }]}
        >
          <TextArea rows={2} placeholder="Describe why goods are being returned…" maxLength={500} showCount />
        </Form.Item>
        <Form.Item name="remarks" label="Remarks (optional)">
          <TextArea rows={1} placeholder="Additional notes…" maxLength={300} />
        </Form.Item>
      </Form>

      <Divider orientation="left" style={{ fontSize: 13 }}>
        Return Items
        <Button size="small" type="dashed" icon={<PlusOutlined />} onClick={addItem} style={{ marginLeft: 12 }}>
          Add Row
        </Button>
      </Divider>

      {!selectedPO && (
        <Alert type="warning" showIcon message="Select a Purchase Order above to load items for return." style={{ marginBottom: 12 }} />
      )}

      <Table
        columns={itemColumns}
        dataSource={items}
        rowKey="key"
        pagination={false}
        size="small"
        scroll={{ x: 760 }}
      />

      {/* Total footer */}
      <div style={{
        marginTop: 12, padding: '10px 16px',
        background: '#fff2f0', borderRadius: 6, border: '1px solid #ffccc7',
        display: 'flex', justifyContent: 'flex-end', gap: 16,
      }}>
        <Text type="secondary" style={{ fontSize: 13 }}>Total Return Amount</Text>
        <Text strong style={{ fontSize: 18, color: '#cf1322' }}>{fmtMoney(totalAmount)}</Text>
      </div>
    </Modal>
  );
};

export default ReturnOrderForm;
